import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ArrowDropDown, Close } from "@mui/icons-material";
import { serviceOptions } from "../../common/const";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { useDispatch } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { updateSesson } from "../../api/sessionApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";

const Calendar = ({
    allEvents,
    getDashboardData
}) => {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [days, setDays] = useState([]);
    const [eventDate, setEventDate] = useState(null);
    const [events, setEvents] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [topic, setTopic] = useState(null);
    const [meetingLink, setMeetingLink] = useState(null);
    const [selectedSession, setSelectedSesion] = useState(null);

    const getNextSevenDays = () => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            dates.push(moment().add(i, 'days').format('YYYY-MM-DD'));
        }
        setDays(dates);
        setEventDate(dates[0]);
    };

    useEffect(() => {
        getNextSevenDays();
    }, []);

    const validateMeetingLink = (url) => {
        const patterns = [
            /https:\/\/meet\.google\.com\/[a-zA-Z0-9-]+/,
            /https:\/\/.*\.zoom\.us\/j\/\d+/,
            /https:\/\/teams\.microsoft\.com\/l\/meetup-join\/.+/,
        ];

        return patterns.some((pattern) => pattern.test(url));
    };

    useEffect(() => {
        setEvents(allEvents?.filter((a) => moment(a?.session_start_time)?.isSame(eventDate, 'day')));
    }, [eventDate]);

    return (
        <Fragment>
            <CommonDialog
                open={addOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Add Topic & Meeting Link</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddOpen(false);
                                    setSelectedSesion(null);
                                    setMeetingLink(null);
                                    setTopic(null);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Topic"
                                type="email"
                                name="topic"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="Enter topic"
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Meeting Link"
                                type="email"
                                name="meetinglink"
                                value={meetingLink}
                                onChange={(e) => setMeetingLink(e.target.value)}
                                placeholder="Enter meeting link"
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Save"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={async () => {
                            try {
                                if (validateMeetingLink(meetingLink) && topic !== null) {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await updateSesson(selectedSession?.mentor_session_id, {
                                        service_name: selectedSession.service_name,
                                        status: selectedSession?.status,
                                        "meeting_link": meetingLink,
                                        topic: topic
                                    });
                                    if (response.success) {
                                        showNotification(response.message, 'success');
                                        setAddOpen(false);
                                        setSelectedSesion(null);
                                        setMeetingLink(null);
                                        setTopic(null);
                                        getDashboardData();
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                    }
                                } else {
                                    showNotification('Please enter a valid meeting link (Meet, Zoom, Teams) & topic', 'error');
                                }
                            } catch (err) {
                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                showNotification(errorMessage, 'error');
                                dispatch(setLoading({
                                    loading: false
                                }));
                            }
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={isMobile ? 1 : 2}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            flexDirection={isMobile ? "column" : "row"}
                            justifyContent="space-between"
                            alignItems={isMobile ? "flex-start" : "center"}
                            gap={isMobile ? 1.5 : 0}
                            sx={{
                                padding: { xs: '12px', sm: '13px', md: '16px' }
                            }}
                        >
                            <Box>
                                <Typography
                                    fontWeight={600}
                                    fontSize={{ xs: 15, sm: 16, md: 18 }}
                                >
                                    Next 7 Days
                                </Typography>
                                <Typography
                                    fontSize={{ xs: 11, sm: 12, md: 14 }}
                                    color={theme.palette.background.dark}
                                >
                                    A quick look at your schedule for the coming week.
                                </Typography>
                            </Box>
                            <CommonButton
                                label="View More"
                                marginBottom="0px"
                                width={isMobile ? "100px" : "160px"}
                                height={isMobile ? "28px" : "30px"}
                                background={theme.palette.background.main}
                                color={theme.palette.background.dark}
                                borderRadius={3}
                                endIcon={<ArrowDropDown sx={{ color: theme.palette.background.dark }} />}
                                fontWeight={600}
                                fontSize={isMobile ? 12 : 14}
                                handleClick={() => navigate('/schedule-calendar')}
                            />
                        </Box>
                    </Fragment>
                }
                content={
                    <Box>
                        <Box
                            display="flex"
                            gap={1}
                            mb={2}
                        >
                            {days.map((a) => (
                                <Box
                                    sx={{
                                        background: eventDate === a ? theme.palette.primary.main : theme.palette.background.main,
                                        borderRadius: 1,
                                        padding: 1,
                                        width: '70px',
                                        cursor: 'pointer',
                                        border: `1px solid ${theme.palette.background.secondary}`
                                    }}
                                    onClick={() => {
                                        setEventDate(a);
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={600} color={theme.palette.secondary.main} textAlign="center">{moment(a).format('ddd')}</Typography>
                                    <Typography fontSize={18} fontWeight={600} color={theme.palette.secondary.main} textAlign="center">{moment(a).date()}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {events.length > 0 ? events.map((a, index) => {
                            return (
                                <CommonCard
                                    key={index}
                                    background={theme.palette.background.main}
                                    borderRadius={2}
                                    paddingBottom="0px"
                                    padding="13px"
                                    marginTop={index === 0 ? "0px" : "10px"}
                                    content={
                                        <Box
                                            display="flex"
                                            flexDirection={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'stretch', sm: 'center' }}
                                            gap={{ xs: 2, sm: 0 }}
                                        >
                                            <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="center"
                                                flex={{ xs: 'none', sm: 1 }}
                                            >
                                                <Box
                                                    sx={{
                                                        background: theme.palette.secondary.main,
                                                        borderRadius: 1,
                                                        padding: 1,
                                                        width: { xs: '55px', sm: '65px' },
                                                        cursor: 'pointer',
                                                        border: `1px solid ${theme.palette.background.secondary}`,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <Typography
                                                        fontSize={{ xs: 17, sm: 19 }}
                                                        fontWeight={600}
                                                        color={theme.palette.primary.main}
                                                        textAlign="center"
                                                    >
                                                        {moment(a.session_start_time).date()}
                                                    </Typography>
                                                    <Typography
                                                        fontSize={{ xs: 10, sm: 11 }}
                                                        fontWeight={600}
                                                        color="White"
                                                        textAlign="center"
                                                    >
                                                        {moment(a.session_start_time).format('MMM YYYY')}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography
                                                        fontSize={{ xs: 13, sm: 14 }}
                                                        fontWeight="600"
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {`${a?.student?.user?.first_name} ${a?.student?.user?.last_name}`}
                                                    </Typography>
                                                    <Typography
                                                        fontSize={{ xs: 13, sm: 14 }}
                                                        fontWeight="600"
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {a.topic}
                                                    </Typography>
                                                    <Typography
                                                        fontSize={{ xs: 13, sm: 14 }}
                                                        fontWeight="600"
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {serviceOptions?.find((i) => i.value === a.service_name)?.label}
                                                    </Typography>
                                                    <Typography
                                                        fontSize={{ xs: 12, sm: 13 }}
                                                        color={theme.palette.background.dark}
                                                    >
                                                        {`${moment(a.session_start_time).format('hh:mm A')} - ${moment(a.session_end_time).format('hh:mm A')}`}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Button Container */}
                                            <Box
                                                display="flex"
                                                gap={1}
                                            >
                                                {a.meeting_link !== null ? (
                                                    <CommonButton
                                                        marginBottom="0px"
                                                        label="Join"
                                                        width="60px"
                                                        height="30px"
                                                        disabled={moment(a.session_start_time).diff(moment(), 'minutes') > 10}
                                                        handleClick={() => {
                                                            window.open(a.meeting_link)
                                                        }}
                                                    />
                                                ) : (
                                                    <CommonButton
                                                        marginBottom="0px"
                                                        label="Add Link"
                                                        width="110px"
                                                        height="30px"
                                                        handleClick={() => {
                                                            setSelectedSesion(a);
                                                            setAddOpen(true);
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            )
                        }) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Session Found</Typography>
                        )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="0px"
            />
        </Fragment>
    )
}

export default Calendar;